import { Suspense, useEffect, useMemo } from 'react';
import {
  ModelScope,
  useActionsContext,
  useInScopeModel,
  useWatchableValue,
} from '../../actions/framework';
import { TaxonomyColumnModel } from './taxonomy-column.model';
import { GraphModel } from '../../graph/graph.model';
import { Assert, assertExists, assertOk } from '@cotera/utilities';
import { ColumnModelContext, useColumnModel } from './context';
import {
  createPath,
  TaxonomyTypeBuilder,
  convertTaxonomyToExpression,
} from '@cotera/client/app/components/taxonomy-type-builder';
import { Path } from '@cotera/client/app/components/forms/hooks/use-form';
import { Expression, Relation, Ty } from '@cotera/era';
import { Tab, useActiveTab, useTabParams } from '../../tab-manager';
import { ErrorBoundary } from 'react-error-boundary';
import { DisplayError, Loading, Title } from '@cotera/client/app/components';
import { GraphDataGrid } from '../../data-grid';
import { TabItem } from '../../components/topbar/tab-item';
import { ChangeIndicator } from '../../components/topbar/change-indicator';
import { Separator } from '../../components/ui/separator';
import { Text } from '@cotera/client/app/components/ui';
import { useEraScopesAwareRelIR } from '@cotera/client/app/pages/apps/compiler/macro-expansion/scopes';
import { FormState } from '@cotera/client/app/components/taxonomy-type-builder/type-builder';

const ColumnEditView: React.FC<{
  dataset: string;
  column: string;
  isNew: boolean;
}> = ({ dataset, column, isNew }) => {
  const { tabId } = useTabParams();
  const graphModel = useInScopeModel(GraphModel);

  Assert.assert(graphModel !== null);

  const model = useMemo(
    () =>
      new TaxonomyColumnModel(
        { dataset, column, graph: graphModel.graph },
        { targeted: true, isNew }
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataset, column]
  );

  const datasetRel = assertOk(
    assertExists(useWatchableValue(model.datasetRel))
  ).rel; // TODO handle null / error case

  const formState = useWatchableValue(model.formState);

  return (
    <>
      <TabViewContainer model={model} tabId={tabId} />
      <ModelScope models={[model]}>
        <ColumnModelContext.Provider value={model}>
          {formState.name !== 'new-column' && (
            <Text.Title title={formState.name} type="title" className="pt-2" />
          )}
          <TaxonomyTypeBuilder
            value={useWatchableValue(model.uiState)}
            onChange={(newNode) => {
              const newExprIR = convertTaxonomyToExpression(
                newNode,
                datasetRel
              );

              const newEraQL = Expression.fromAst(newExprIR).toEraQL();

              model.eraQL.set(newEraQL);
            }}
            onEditClick={() => {}} // TODO later
            rel={datasetRel}
            actions={[]}
            columnName={column}
            context={(props) => (
              <ItemContext path={props.path} formState={props.formState} />
            )}
          />
          <Separator className="my-6" />
          <PreviewSection />
        </ColumnModelContext.Provider>
      </ModelScope>
    </>
  );
};

const PreviewSection: React.FC = () => {
  const model = useColumnModel();
  const preview = useWatchableValue(model.preview);

  return (
    <div className="flex flex-col w-full">
      <Title className="mb-2">Preview </Title>
      {preview === null ? (
        <div>
          <Text.Caption>Run the taxonomy column to see preview. </Text.Caption>
        </div>
      ) : (
        <PreviewRel />
      )}
    </div>
  );
};

const PreviewRel: React.FC = () => {
  const model = useColumnModel();
  const previewRel = useWatchableValue(model.preview)!;
  const relIr = useEraScopesAwareRelIR(previewRel);
  const builtRel = Relation.wrap(relIr);

  return (
    <div className="w-full h-full">
      <GraphDataGrid rel={builtRel} relName={model.id} shouldShow={true} />
    </div>
  );
};

const ItemContext: React.FC<{
  path: Path;
  formState: FormState;
}> = ({ path, formState }) => {
  const makePath = (path: Path) => createPath(['root', ...path], formState);
  /**
   * this builds up the enum path by getting the name for each point in the form state
   * @param path The full path to build up
   * @param currentPath The current path being processed
   * @returns Array of names forming the enum path
   */
  const makeEnumPath = (currentPath: Path): string[] => {
    const next = path.at(currentPath.length);

    if (
      path.length === 0 ||
      next === undefined ||
      currentPath.length >= path.length
    ) {
      return [];
    }

    const nextPath = [...currentPath, next];
    const fieldName = formState.getField(makePath(nextPath))?.name;

    if (!fieldName) {
      return [];
    }

    return [fieldName, ...makeEnumPath(nextPath)];
  };

  const enumPath = Ty.enumPath(...makeEnumPath([]));
  return (
    enumPath.length > 0 && null //TODO replace with something that shows the percentage
  );
};

export const NewTaxonomyColumn: React.FC = () => {
  const { id: dataset } = useTabParams<{ id: string }>();

  return (
    <SuspensefulColumnEditView
      dataset={dataset}
      column="new-column"
      isNew={true}
    />
  );
};

export const EditTaxonomyColumn: React.FC = () => {
  const { id: dataset, columnId: column } = useTabParams<{
    id: string;
    columnId: string;
  }>();
  return (
    <SuspensefulColumnEditView
      dataset={dataset}
      column={column}
      isNew={false}
    />
  );
};

const SuspensefulColumnEditView: React.FC<{
  dataset: string;
  column: string;
  isNew: boolean;
}> = ({ dataset, column, isNew }) => {
  return (
    <ErrorBoundary
      fallbackRender={(e) => (
        <DisplayError
          error={{
            message: JSON.stringify(e.error),
          }}
        />
      )}
    >
      <Suspense fallback={<Loading.Dots />}>
        <ColumnEditView dataset={dataset} column={column} isNew={isNew} />
      </Suspense>
    </ErrorBoundary>
  );
};

const TabViewContainer: React.FC<{
  model: TaxonomyColumnModel;
  tabId: string;
}> = ({ model, tabId }) => {
  return (
    <Tab tabId={tabId}>
      <TabView model={model} tabId={tabId} />
    </Tab>
  );
};

const TabView: React.FC<{
  model: TaxonomyColumnModel;
  tabId: string;
}> = ({ model, tabId }) => {
  const { name } = useWatchableValue(model.formState);
  const activeTab = useActiveTab();
  const actionsContext = useActionsContext();

  useEffect(() => {
    if (activeTab === tabId) {
      actionsContext.targetModel(model);
    } else {
      actionsContext.markAsNotTargeted(model);
    }
  }, [tabId, activeTab, name, actionsContext, model]);

  return (
    <TabItem
      tab={{
        id: tabId,
        title: `${model.inputs.dataset}/${name || 'New LLM Column'}`,
        showClose: false,
        tabView: <ChangeIndicator model={model} tabId={tabId} />,
      }}
    />
  );
};
